<template>
  <v-row no-gutters>
    <v-col cols="12" lg="12">
      <CardSimple v-if="isRequired('system-demand-parameters')" title="System Demand Parameters">
        <v-row>
          <v-col cols="12" lg="6">
            <InputYearly
              v-model="computedInput.demand_peak"
              :error-messages="$utils.getIfExists(errors, ['input', 'demand_peak'])"
              :label="schema.demand_peak.title"
              :hint="schema.demand_peak.tooltip"
              :suffix="schema.demand_peak.unit"
              type="number"
              :num_years="num_years"
              :start-year="startYear"
              fill
              spread-values
              :fill-value="schema.demand_peak.items.default"
              chart-visible
              @change="$emit('change')"
            />
          </v-col>
          <v-col cols="12" lg="6">
            <InputYearly
              v-model="computedInput.demand_annual"
              :error-messages="$utils.getIfExists(errors, ['input', 'demand_annual'])"
              :label="schema.demand_annual.title"
              :hint="schema.demand_annual.tooltip"
              :suffix="schema.demand_annual.unit"
              type="number"
              :num_years="num_years"
              :start-year="startYear"
              fill
              spread-values
              :fill-value="schema.demand_annual.items.default"
              chartVisible
              @change="$emit('change')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" lg="12">
            <InputYearlyProfile
              v-model="computedInput.demand_profile"
              :title="schema.demand_profile.title"
              :suffix="schema.demand_profile.unit"
              :hint="schema.demand_profile.tooltip"
              :error-messages="$utils.getIfExists(errors, ['input', 'demand_profile'])"
              :num_years="num_years"
              :start-year="startYear"
              profile-type="DEMAND"
              :scaleProfile="
                (data, year) =>
                  scale(
                    data,
                    input.demand_peak[year],
                    input.demand_annual[year],
                    year - 1 + startYear
                  )
              "
              fill
              @change="$emit('change')"
            />
          </v-col>
        </v-row>
      </CardSimple>
    </v-col>
    <v-col cols="12" lg="12">
      <Aggregate
        v-if="isRequired('nodal-demands')"
        title="Nodal Demands"
        v-model="computedInput.demands"
        :ItemConstructor="demandFactory"
        :headers="demandHeaders"
        v-bind="$attrs"
        copy-btn
      >
        <template v-slot:[`item.bus_id`]="{ item }">
          <v-select v-model="item.bus_id" :items="nodeIndex" single-line @change="$emit('change')">
            <template #selection="data">
              {{ data.item.name }}
              <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
            </template>
            <template #item="data">
              {{ data.item.name }}
              <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
            </template>
          </v-select>
        </template>
        <template v-slot:[`item.method`]="{ item }">
          <v-select
            v-model="item.method"
            :items="nodalDemandMethodItems"
            :label="schemaDemand.method.title"
            single-line
            @change="$emit('change')"
          />
        </template>
        <template v-slot:[`item.active`]="{ item }">
          <v-switch
            v-model="item.active"
            :label="schemaDemand.active.title"
            dense
            @change="$emit('change')"
          ></v-switch>
        </template>
        <template #default="{ item }">
          <v-card class="pa-2 ma-2" flat v-if="item.method">
            <v-card-text>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">General</p>
                <span v-if="item.method === 'DEMAND_PARTICIPATION_FACTOR'">
                  <InputYearlyProfile
                    v-model="item.active_power"
                    label="Active Power Participation"
                    hint="Percentage of the system demand"
                    suffix="%"
                    :num_years="num_years"
                    :start-year="startYear"
                    fill
                    object-value
                    items-type="constant"
                    @change="$emit('change')"
                  />
                  <InputYearlyProfile
                    v-model="item.reactive_power"
                    label="Reactive Power"
                    hint="Not a participation factor unlike the active power"
                    suffix="MVAR"
                    :num_years="num_years"
                    :start-year="startYear"
                    fill
                    object-value
                    items-type="constant"
                    @change="$emit('change')"
                  />
                </span>
                <span v-else-if="item.method === 'CONSTANT_POWER'">
                  <InputYearlyProfile
                    v-model="item.active_power"
                    label="Active Power"
                    :suffix="schemaDemand.active_power.unit"
                    :num_years="num_years"
                    :start-year="startYear"
                    fill
                    :fill-value="schemaDemand.active_power.items.default.value"
                    object-value
                    items-type="constant"
                    @change="$emit('change')"
                  />
                  <InputYearlyProfile
                    v-model="item.reactive_power"
                    label="Reactive Power"
                    :suffix="schemaDemand.reactive_power.unit"
                    :num_years="num_years"
                    :start-year="startYear"
                    fill
                    :fill-value="schemaDemand.reactive_power.items.default.value"
                    object-value
                    items-type="constant"
                    @change="$emit('change')"
                  />
                </span>
                <span v-else-if="item.method === 'PROFILE'">
                  <InputYearlyProfile
                    v-model="item.active_power"
                    :label="schemaDemand.active_power.title"
                    :suffix="schemaDemand.active_power.unit"
                    :num_years="num_years"
                    :start-year="startYear"
                    fill
                    object-value
                    items-type="profile"
                    profile-type="DEMAND"
                    @change="$emit('change')"
                    :scaleProfile="
                      item.active_power_scaling.active
                        ? (data, year) =>
                            scale(
                              data,
                              item.active_power_scaling.peak[year],
                              item.active_power_scaling.annual[year],
                              year - 1 + startYear
                            )
                        : null
                    "
                  />
                  <v-switch
                    v-model="item.active_power_scaling.active"
                    color="primary"
                    label="Active Profile Scaling"
                    @change="$emit('change')"
                  >
                  </v-switch>
                  <v-row v-if="item.active_power_scaling.active">
                    <v-col cols="12" lg="6">
                      <InputYearly
                        v-model="item.active_power_scaling.peak"
                        hint="Peak Demand"
                        label="Peak Demand"
                        hide-divider
                        type="number"
                        fill
                        chart-visible
                        suffix="MW"
                        :num_years="num_years"
                        :start-year="startYear"
                        :fill-value="schema.demand_peak.items.default"
                        @change="$emit('change')"
                      ></InputYearly>
                    </v-col>
                    <v-col cols="12" lg="6">
                      <InputYearly
                        v-model="item.active_power_scaling.annual"
                        hint="Annual Demand"
                        label="Annual Demand"
                        hide-divider
                        type="number"
                        fill
                        chart-visible
                        suffix="GWh"
                        :num_years="num_years"
                        :start-year="startYear"
                        :fill-value="schema.demand_annual.items.default"
                        @change="$emit('change')"
                      ></InputYearly></v-col
                  ></v-row>
                  <InputYearlyProfile
                    v-model="item.reactive_power"
                    :label="schemaDemand.reactive_power.title"
                    :suffix="schemaDemand.reactive_power.unit"
                    :num_years="num_years"
                    :start-year="startYear"
                    fill
                    object-value
                    items-type="profile"
                    profile-type="DEMAND"
                    @change="$emit('change')"
                    :scaleProfile="
                      item.reactive_power_scaling.active
                        ? (data, year) =>
                            scale(
                              data,
                              item.reactive_power_scaling.peak[year],
                              item.reactive_power_scaling.annual[year],
                              year - 1 + startYear
                            )
                        : null
                    "
                  />
                  <v-switch
                    v-model="item.reactive_power_scaling.active"
                    color="primary"
                    label="Reactive Profile Scaling"
                    @change="$emit('change')"
                  >
                  </v-switch>
                  <v-row v-if="item.reactive_power_scaling.active">
                    <v-col cols="12" lg="6">
                      <InputYearly
                        v-model="item.reactive_power_scaling.peak"
                        hint="Peak Demand"
                        label="Peak Demand"
                        hide-divider
                        type="number"
                        fill
                        chart-visible
                        suffix="MW"
                        :num_years="num_years"
                        :start-year="startYear"
                        :fill-value="schema.demand_peak.items.default"
                        @change="$emit('change')"
                      ></InputYearly>
                    </v-col>
                    <v-col cols="12" lg="6">
                      <InputYearly
                        v-model="item.reactive_power_scaling.annual"
                        hint="Annual Demand"
                        label="Annual Demand"
                        hide-divider
                        type="number"
                        fill
                        chart-visible
                        suffix="GWh"
                        :num_years="num_years"
                        :start-year="startYear"
                        :fill-value="schema.demand_annual.items.default"
                        @change="$emit('change')"
                      ></InputYearly></v-col
                  ></v-row>
                </span>
                <InputYearly
                  v-model="item.voll"
                  :label="schemaDemand.voll.title"
                  hint="Cost of unserved electricity demand"
                  :num_years="num_years"
                  :start-year="startYear"
                  persistent-hint
                  fill
                  :suffix="schemaDemand.voll.unit"
                  type="number"
                  @change="$emit('change')"
                />
              </v-card>
            </v-card-text>
          </v-card>
        </template>
      </Aggregate>
    </v-col>
    <v-col cols="12" lg="12">
      <SystemDemandResponse
        v-if="isRequired('demand-response')"
        v-model="computedInput.demand_response"
        :error-messages="$utils.getIfExists(errors, ['input', 'demand_response'])"
        :reserves="input.reserves"
        :num_years="num_years"
        :start-year="startYear"
        @change="$emit('change')"
      ></SystemDemandResponse>
    </v-col>
  </v-row>
</template>

<script>
import { CaseModel, schemas } from "data-dictionary";
import { mapGetters } from "vuex";
import inputSelectionMixin from "../mixins/inputSelectionMixin";

export default {
  name: "SystemDemand",
  components: {
    Aggregate: () => import("@/components/Aggregate.vue"),
    SystemDemandResponse: () => import("@/components/SystemDemandResponse.vue"),
    CardSimple: () => import("@/components/CardSimple.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
  },
  mixins: [inputSelectionMixin],
  props: {
    input: { type: Object, required: true },
    settings: { type: Object, required: true },
    num_years: { type: Number, required: true },
    startYear: {
      type: Number,
      required: true,
    },
    errors: {
      type: Object,
      required: false,
    },
  },
  computed: {
    nodalDemandMethodItems() {
      if (this.settings.POWER_FLOW.source === "Production Cost")
        return [{ text: "Participation Factor", value: "DEMAND_PARTICIPATION_FACTOR" }];
      return [
        { text: "Constant Power", value: "CONSTANT_POWER" },
        { text: "Participation Factor", value: "DEMAND_PARTICIPATION_FACTOR" },
        { text: "Profile", value: "PROFILE" },
      ];
    },
    nodalDemands() {
      return this.computedInput.demands.length;
    },
    nodeIndex() {
      return this.computedInput.nodes
        ? this.computedInput.nodes.map((el) => ({
            name: el.name,
            value: el.id,
          }))
        : [];
    },
    ...mapGetters({
      getProfile: "profiles/get",
    }),
    computedInput: {
      get() {
        return this.input;
      },
      set(val) {
        this.$emit("update:input", val);
        this.$emit("change");
      },
    },
    computedCaseSettings() {
      return this.settings;
    },
  },
  data() {
    return {
      demandDefaultProfile: this.input.demand_profile[0],
      demandHeaders: [
        { text: "Name", value: "name" },
        { text: "Connected Node", value: "bus_id" },
        { text: "Method", value: "method" },
        { text: "Active", value: "active" },
      ],
      schemaDemand: {
        ...schemas.filter((v) => v.$id === "demand.schema.json")[0].properties,
        ...schemas
          .filter((v) => v.$id === "demand.schema.json")[0]
          .allOf.map((v) => v.then.properties)
          .reduce((obj, item) => ({ ...obj, ...item }), {}),
      },
      schema: schemas.filter((v) => v.$id === "input.schema.json")[0].properties,
      demandChartOptions: {
        responsive: true,
        scales: {
          xAxes: [
            {
              display: true,
              scaleLabel: {
                display: true,
                labelString: "Hour",
              },
            },
          ],
          yAxes: [
            {
              display: true,
              scaleLabel: {
                display: true,
                labelString: "Demand (MW)",
              },
            },
          ],
        },
        zoom: {
          enabled: false,
          mode: "xy",
        },
        pan: {
          enabled: false,
          mode: "xy",
        },
        plugins: {
          datalabels: {
            display: false,
          },
        },
      },
    };
  },
  watch: {
    nodalDemands(val) {
      const copyInput = this.computedInput;
      if (val) {
        const defaultProfile = {
          id: this.demandDefaultProfile,
          _resource: "PROFILE",
        };
        copyInput.demands = copyInput.demands.map((demand) => {
          const demandCopy = demand;
          if (!demand.active_power[0]) {
            demandCopy.active_power = Array(this.num_years).fill(defaultProfile);
            demandCopy.reactive_power = Array(this.num_years).fill(defaultProfile);
          }
          return demandCopy;
        });
      }
      this.computedInput = copyInput;
    },
  },
  methods: {
    demandFactory: CaseModel.Demand,
    /**
     * [Deprecated] Old version for scaling a demand profile.
     *
     * @param {number} energyDemand
     * @param {number} peakDemand
     * @param {Array<number>} profile
     */
    scaleDemandProfile(energyDemand, peakDemand, profile) {
      if (profile) {
        const maxProfile = Math.max(...profile);
        const numPeaks = profile.filter((e) => e === maxProfile).length;
        const energy = profile.reduce((a, e) => a + e);
        const scale =
          (energyDemand * 1000 - peakDemand * numPeaks) / (energy - maxProfile * numPeaks);
        const profileOutput = profile.map((val) => (val === maxProfile ? peakDemand : scale * val));
        return profileOutput;
      }
      return [];
    },
    /**
     * Scale demand (pMW) to desired peak and energy values.
     * Leaves the original values unmodified, returns a number array.
     */
    scale(values, peak, annual, year) {
      let numbers;
      if (typeof values[0] === "number") {
        numbers = values;
      } else {
        numbers = values.map((v) => v.value);
      }
      const isLeapYear = year % 4 === 0;
      const numHours = isLeapYear ? 366 * 24 : 365 * 24;
      // annual is in GWh, peak in MW
      const desiredAvg = (annual * 1000) / numHours;
      if (peak < desiredAvg) {
        throw new Error("Peak Load too low relative to Annual Load");
      }
      const currentPeak = numbers.reduce((x, y) => (x > y ? x : y), 0);
      const currentAvg = numbers.reduce((x, y) => x + y, 0) / numbers.length;
      const factor = (peak - desiredAvg) / (currentPeak - currentAvg);
      return numbers.map((profPoint) => {
        const ret = desiredAvg + factor * (profPoint - currentAvg);
        return ret;
      });
    },
  },
};
</script>

<style></style>
